<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

defineProps({
  keepAlive: {
    type: Boolean,
    default: true,
  },
});

const route = useRoute();

const isTeamsSettingsRoute = computed(() =>
  ['configuracoes_equipes', 'settings_teams_list'].includes(route.name)
);

const isProfileSettingsRoute = computed(() =>
  ['profile_settings_index', 'configuracoes_perfil'].includes(route.name)
);

const isAgentsSettingsRoute = computed(() => route.name === 'agent_list');

// Integrações reaproveita exatamente o chrome de Usuários: mesmas margens
// laterais e o mesmo cabeçalho fixo (com a borda inferior) no outlet.
const isIntegracoesSettingsRoute = computed(
  () => route.name === 'configuracoes_integracoes'
);

// Respostas prontas segue o mesmo padrão de Usuários e Integrações.
const isCannedSettingsRoute = computed(() => route.name === 'canned_list');

const isTeamNumbersSettingsRoute = computed(
  () => ['team_numbers_list', 'signature_settings'].includes(route.name)
);
</script>

<template>
  <div
    class="settings-wrapper flex flex-col w-full h-full m-0 pb-8 pt-4 overflow-auto bg-n-surface-1"
    :class="{
      'settings-wrapper--teams': isTeamsSettingsRoute,
      'settings-wrapper--profile': isProfileSettingsRoute,
      'px-6':
        !isTeamsSettingsRoute &&
        !isProfileSettingsRoute &&
        !isAgentsSettingsRoute &&
        !isIntegracoesSettingsRoute &&
        !isCannedSettingsRoute &&
        !isTeamNumbersSettingsRoute,
      'settings-wrapper--agents':
        isAgentsSettingsRoute ||
        isIntegracoesSettingsRoute ||
        isCannedSettingsRoute ||
        isTeamNumbersSettingsRoute,
      'settings-wrapper--team-numbers': isTeamNumbersSettingsRoute,
    }"
  >
    <div
      v-if="
        isTeamsSettingsRoute ||
        isAgentsSettingsRoute ||
        isIntegracoesSettingsRoute ||
        isCannedSettingsRoute ||
        isTeamNumbersSettingsRoute
      "
      class="settings-wrapper__teams-header-outlet"
    />
    <div
      class="settings-wrapper__content flex items-start w-full"
      :class="
        isTeamsSettingsRoute
          ? 'settings-wrapper__content--teams'
          : isProfileSettingsRoute
            ? 'settings-wrapper__content--profile'
            : isAgentsSettingsRoute ||
                isIntegracoesSettingsRoute ||
                isCannedSettingsRoute ||
                isTeamNumbersSettingsRoute
              ? 'settings-wrapper__content--agents scrollbar-thin'
              : 'max-w-5xl mx-auto'
      "
    >
      <router-view v-slot="{ Component }">
        <keep-alive v-if="keepAlive">
          <component :is="Component" :key="route.fullPath" />
        </keep-alive>
        <component :is="Component" v-else :key="route.fullPath" />
      </router-view>
    </div>
  </div>
</template>

<style scoped>
.settings-wrapper--team-numbers {
  background: #f5f5f3;
}

.settings-wrapper--agents.settings-wrapper--team-numbers
  .settings-wrapper__teams-header-outlet {
  position: relative;
  z-index: 2;
  width: auto;
  margin: 0 48px;
  padding: 36px 0 0;
  background: #f5f5f3;
  box-shadow: none;
}

.settings-wrapper--team-numbers .settings-wrapper__teams-header-outlet::before {
  display: none;
}

.settings-wrapper--team-numbers
  .settings-wrapper__content--agents
  :deep(.font-inter > main) {
  padding: 0;
}

@media (max-width: 720px) {
  .settings-wrapper--agents.settings-wrapper--team-numbers
    .settings-wrapper__teams-header-outlet {
    margin: 0 16px;
    padding-top: 20px;
  }
}

.settings-wrapper--teams {
  position: relative;
  padding-top: 0;
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  background: #f6f6f6;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.42) transparent;
}

/*
  No mobile o recuo lateral de Equipes passa a 16px e vale para o cabecalho e
  para o corpo. Esta e a unica origem do espacamento lateral da rota: a pagina
  nao repete margem negativa nem padding proprio para chegar a borda.
*/
@media (max-width: 767px) {
  .settings-wrapper--teams {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  /*
    No desktop este ::before sangra para os lados para cobrir o padding do
    wrapper. No mobile qualquer sangramento vira rolagem horizontal: medido em
    428px, o outlet tem 396px e o ::before esticava o scrollWidth para 412px.
    Como o proprio wrapper ja e #f6f6f6, a mesma cor, nao ha nada a cobrir --
    zerar o sangramento remove a rolagem sem mudar o visual.
  */
  .settings-wrapper--teams .settings-wrapper__teams-header-outlet::before {
    inset: 0;
  }
}

.settings-wrapper--agents {
  position: relative;
  padding: 0;
  background: #f6f6f6;
  overflow: hidden;
}

.settings-wrapper__content--agents {
  position: relative;
  z-index: 1;
  max-width: none;
  min-height: 0;
  margin: 0;
  flex: 1 1 auto;
  overflow-y: auto;
}

.settings-wrapper__content--agents :deep(.font-inter > main) {
  padding: 1rem 1.5rem;
}

@media (min-width: 768px) {
  .settings-wrapper__content--agents :deep(.font-inter > main) {
    padding-left: 2.5rem;
    padding-right: 2.5rem;
  }
}

.settings-wrapper__teams-header-outlet {
  /* Header fixo dentro do scroll da página de equipes. */
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;
  flex-shrink: 0;
  padding-top: 1rem;
  background: #f6f6f6;
  box-shadow: 0 1px 0 rgba(226, 232, 240, 0.9);
  isolation: isolate;
}

.settings-wrapper__teams-header-outlet::before {
  content: '';
  position: absolute;
  inset: 0 -2.5rem;
  z-index: -1;
  background: #f6f6f6;
}

.settings-wrapper--agents .settings-wrapper__teams-header-outlet {
  width: auto;
  margin-left: 2.5rem;
  margin-right: 2.5rem;
}

.settings-wrapper--teams::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.settings-wrapper--teams::-webkit-scrollbar-track {
  background: transparent;
}

.settings-wrapper--teams::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.42);
  border-radius: 999px;
}

.settings-wrapper__content--teams {
  position: relative;
  z-index: 1;
  max-width: none;
  margin-left: 0;
  margin-right: 0;
}

.settings-wrapper--profile {
  flex: 1 1 auto;
  height: 100%;
  min-height: calc(100dvh - 4rem);
  margin: 0;
  padding: 0;
  padding-bottom: 0;
  background: #f6f6f6;
  overflow: hidden;
}

.settings-wrapper__content--profile {
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: calc(100dvh - 4rem);
  flex: 1;
  align-items: stretch;
  max-width: none;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 0;
  padding-bottom: 0;
}

.settings-wrapper__content--profile > :deep(.acl-profile-settings-workspace) {
  align-self: stretch;
  flex: 1 1 auto;
  min-height: 100%;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>
